import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Globe, Users, Award, Building, Target, Heart,
  ArrowRight, Play, CheckCircle
} from "lucide-react";

const values = [
  {
    icon: Globe,
    title: "Global Perspective",
    description: "We believe education knows no borders. Our network spans continents to create opportunities everywhere.",
  },
  {
    icon: Heart,
    title: "Student-First",
    description: "Every decision we make centers on student success. Your journey is our priority.",
  },
  {
    icon: Award,
    title: "Excellence",
    description: "We partner only with accredited institutions and maintain the highest standards in all we do.",
  },
  {
    icon: Users,
    title: "Community",
    description: "Our alumni network is a lifelong resource. You're joining a global family of achievers.",
  },
];

const stats = [
  { value: "2013", label: "Founded" },
  { value: "50+", label: "Countries" },
  { value: "200+", label: "Partner Institutions" },
  { value: "10,000+", label: "Alumni Network" },
];

const AboutPage = () => {
  return (
    <main className="pt-20">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-[#060B19] to-[#060B19]" />
        <div className="absolute top-1/4 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[200px]" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
                About M2L
              </span>
              <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
                Connecting Talent with{" "}
                <span className="gradient-text">Global Opportunity</span>
              </h1>
              <p className="text-xl text-white/60 leading-relaxed mb-8">
                Since 2013, Management To Learn USA has been bridging the gap between 
                ambitious students and world-class educational opportunities. Our mission 
                is simple: transform lives through the power of international education.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/services"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-[#060B19] rounded-lg font-semibold hover:bg-primary-hover transition-all group"
                >
                  Our Services
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/heritage"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-secondary/30 text-secondary rounded-lg font-medium hover:bg-secondary/10 transition-all"
                >
                  Our Heritage
                </Link>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative"
            >
              <div className="aspect-video rounded-2xl overflow-hidden glass-card">
                <img
                  src="https://www.m2l-usa.com/wp-content/uploads/2017/10/College-students-e1510941833125.jpg"
                  alt="M2L Students"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <a
                    href="https://vimeo.com/243340335"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-16 h-16 rounded-full bg-primary/20 border border-primary/40 flex items-center justify-center hover:bg-primary/30 transition-all"
                  >
                    <Play className="w-6 h-6 text-primary ml-1" />
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-surface-solid border-y border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-heading font-black text-primary mb-1">
                  {stat.value}
                </div>
                <div className="text-white/50 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-primary" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">Our Mission</h3>
              <p className="text-white/60 leading-relaxed">
                To improve society by educating citizens and aligning institutions, 
                contributing to the promotion of a more inclusive and open world. We 
                seek new opportunities for academic growth to connect local demands 
                with global best practices.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="glass-card rounded-2xl p-8"
            >
              <div className="w-14 h-14 rounded-xl bg-secondary/20 border border-secondary/30 flex items-center justify-center mb-6">
                <Building className="w-7 h-7 text-secondary" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-white mb-4">Our Vision</h3>
              <p className="text-white/60 leading-relaxed">
                To be the leading bridge between international students and world-class 
                educational institutions, fostering knowledge transfer, innovation, and 
                talent development that contributes to the growth of nations worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-surface-solid">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
              What We Stand For
            </span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white">
              Our Core Values
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card rounded-2xl p-6 text-center card-hover"
              >
                <div className="w-14 h-14 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mx-auto mb-4">
                  <value.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="font-heading text-xl font-bold text-white mb-3">{value.title}</h3>
                <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="py-20 bg-[#060B19]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-primary text-sm font-medium tracking-[0.2em] uppercase mb-4 block">
                What We Offer
              </span>
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
                Comprehensive Support for Your Journey
              </h2>
              <p className="text-white/60 leading-relaxed mb-8">
                From the moment you decide to pursue international education to your 
                graduation and beyond, we're with you every step of the way.
              </p>

              <ul className="space-y-4">
                {[
                  "Housing support and accommodation assistance",
                  "F1 visa documentation and guidance",
                  "Health insurance application and benefits",
                  "University admission assistance",
                  "Academic progress monitoring",
                  "Institutional liaison and support",
                ].map((item, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <span className="text-white/80">{item}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/services"
                className="inline-flex items-center gap-2 mt-8 text-primary hover:underline"
              >
                View All Services
                <ArrowRight className="w-4 h-4" />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 gap-4"
            >
              <img
                src="https://www.m2l-usa.com/wp-content/uploads/2017/10/Escola-de-pilotagem-Florida_PEDRO-min-1024x768.jpg"
                alt="Student success"
                className="rounded-xl object-cover h-48 w-full"
              />
              <img
                src="https://www.m2l-usa.com/wp-content/uploads/2017/11/IMG_2496-min.jpg"
                alt="Campus life"
                className="rounded-xl object-cover h-48 w-full mt-8"
              />
              <img
                src="https://www.m2l-usa.com/wp-content/uploads/2017/11/1A_Houston_Crew_IMG_8049-min.jpg"
                alt="Team"
                className="rounded-xl object-cover h-48 w-full"
              />
              <img
                src="https://www.m2l-usa.com/wp-content/uploads/2018/02/reading.jpg"
                alt="Learning"
                className="rounded-xl object-cover h-48 w-full mt-8"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-primary/10 via-[#060B19] to-primary/10 border-y border-white/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-white mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-white/60 mb-8">
            Connect with our team and discover how we can help you achieve your 
            educational goals.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-[#060B19] rounded-lg font-semibold hover:bg-primary-hover transition-all group"
          >
            Get in Touch
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
